import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Form, Formik } from 'formik';
import { List, type RecordOf } from 'immutable';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuidv4 } from 'uuid';
import { DateTime } from 'luxon';

import CloseIcon from '@mui/icons-material/ClearRounded';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import type { Theme } from '@mui/material/styles';

import { billPresentmentSelectors } from 'app-components/flux/bill-presentment';
import { accountsSelectors } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import {
  chartOfAccountsSelectors,
  chartOfAccountsTypes,
  chartOfAccountsUtils,
} from 'app-components/flux/chart-of-accounts';
import { dateSelectors } from 'app-components/flux/date';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { preferencesV2Selectors } from 'app-components/flux/preferences-v2';
import { rootUiActions } from 'app-components/flux/root-ui';
import { getDrawerSx } from 'components/Drawers/drawerStyles';
import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';
import {
  customFrequencyUtils,
  scheduledTransactionsActions,
  scheduledTransactionsTypes,
  scheduledTransactionsUtils,
} from 'app-components/flux/scheduled-transactions';
import type { RecurrenceType } from 'app-components/flux/scheduled-transactions/customFrequencyTypes';
import { FrequencyEnum } from 'app-components/flux/scheduled-transactions/frequency';
import { getInitialSeriesSplitForScheduledTransactionEditForm as getInitialSeriesSplitForForm } from 'app-components/flux/scheduled-transactions/scheduledTransactionEditForm';
import {
  AutoAdjustAmountEnum,
  type ScheduledTransaction,
  type ScheduledTransactionType,
  mkScheduledTransaction,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { transactionsTypes, transactionsUtils } from 'app-components/flux/transactions';
import type { SplitProps, TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { tracker } from 'app-components/utils/core';
import { toPlainIfImmutable } from 'app-components/utils/core/immutableHelpers';

import type { ProjectSelect } from 'components/ClientProjectSelect';
import DevMenuButton from 'components/DevMenuButton';
import showConfirmationDialog from 'components/Dialogs/ConfirmationDialog/show';
import StdDialog from 'components/Dialogs/StdDialog';
import QButton from 'components/QButton';
import { dispatchGeneratorDialog } from 'components/Transactions/GeneratorDialog/dispatcher';
import { showSplitDialog } from 'components/Transactions/SplitDialog/actions';
import {
  doesSplitModeAllowPaymentOrnaments,
  isOverflowSplitItem,
  normalizeSeriesSplitForPersistence,
  splitItemsToArray,
  splitToPlain,
  type PlainSplitProps,
} from 'app-components/flux/transactions/splitsHelpers';
import { useAppDispatch, useAppSelector, useAppStore } from 'hooks/redux';

import BasicTab from './tabs/basicTab';
import BillConnectTab from './tabs/billConnectTab';
import { shouldKeepManualIncomeIncludedOnSubmit } from './tabs/categorySelection';
import FrequencyTab from './tabs/frequencyTab';
import ScheduledTransactionRootModalDrawer from './ScheduledTransactionRootModalDrawer';

import { matchCriteriaOptions } from './types';
import type { FormikScheduledTxn, MatchOptions, STFormikProps } from './types';
import type { ScheduledTransactionEditDialogProps } from '../types';
import { dispatchRequestDialog } from 'components/RequestDialog/dispatcher';
import Dump from 'components/Dump';

type SubmittedSeriesTransaction = TransactionProps & {
  excludeF2SDest?: boolean;
  excludeF2SUserOverridden?: boolean;
};

const useStyles = makeStyles()((theme: Theme) => ({
  tabs: {
    display: 'flex',
    flexShrink: 0,
    padding: theme.spacing(3),
    paddingTop: theme.spacing(1),
    paddingBottom: 0,
    backgroundColor: theme.palette.background.paper,
    '& .MuiTabs-scroller': {
      borderBottom: `1px solid ${theme.palette.border.main}`,
    },
  },
  tabPanel: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
    borderBottom: `1px solid ${theme.palette.border.main}`,
    '& .MuiDialogContent-root': {
      padding: theme.spacing(3),
    },
  },
  billConnect: {
    display: 'flex',
    flexDirection: 'column',
  },
  hidden: {
    display: 'none',
  },
  frequencyHeader: {
    display: 'flex',
    padding: '14px 24px',
    backgroundColor: theme.palette.background.default,
  },
  titleText: {
    display: 'flex',
    justifySelf: 'center',
    fontSize: 24,
    fontWeight: '400',
    flexGrow: 1,
    justifyContent: 'center',
    paddingLeft: '40',
  },
  closeButton: {
    display: 'flex',
    justifySelf: 'flex-end',
    color: theme.palette.text.secondary,
    flexGrow: 0,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
}));

const stripOverflowMemoFromSplit = (splitToStrip: PlainSplitProps): PlainSplitProps => {
  const items = splitToStrip.items ?? [];
  return {
    ...splitToStrip,
    items: items.map((item) => (isOverflowSplitItem(item) ? { ...item, isOverflow: true } : item)),
  };
};

/** Series template transaction as plain props (Immutable.Record or POJO). */
function plainSeriesTemplateTransaction(scheduledTransaction: ScheduledTransactionType): TransactionProps | undefined {
  const raw = toPlainIfImmutable(scheduledTransaction?.transaction);
  if (raw == null || typeof raw !== 'object') return undefined;
  return raw as TransactionProps;
}

const getInitialDate = (model: any, txn?: any, currentDate?: DateTime) => {
  const dueOnDate = model?.dueOn && DateTime.fromISO(model.dueOn);
  if (dueOnDate?.isValid) {
    return dueOnDate;
  }
  const txnDate = txn?.postedOn && DateTime.fromISO(txn.postedOn);
  if (txnDate?.isValid) {
    return txnDate;
  }
  return currentDate;
};

const getInitialAmount = (scheduledTransaction: ScheduledTransactionType) => {
  let criteria: MatchOptions;
  if (scheduledTransaction?.matchAnyAmount) criteria = matchCriteriaOptions.ANY_AMOUNT;
  else if (
    scheduledTransaction?.matchAmountVariance &&
    scheduledTransaction?.matchAmountVariance > 0 &&
    !scheduledTransaction?.resetMatchAmountVariance
  )
    criteria = matchCriteriaOptions.LIMITED_RANGE;
  else if (scheduledTransaction?.matchAmountVariance === 0 && !scheduledTransaction?.resetMatchAmountVariance)
    criteria = matchCriteriaOptions.EXACT_AMOUNT;
  else criteria = matchCriteriaOptions.AUTO_MATCH;

  return criteria;
};

// ===== Tab Panel =====
type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};
function TabPanel(props: TabPanelProps) {
  const { classes } = useStyles();
  const { children, value, index, ...other } = props;
  return (
    <div
      role='tabpanel'
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      className={value === index ? classes.tabPanel : classes.hidden}
    >
      {value === index && children}
    </div>
  );
}

// ===== Form Content =====
interface FormProps {
  formikProps: STFormikProps;
  create?: boolean;
  scheduledTransaction: scheduledTransactionsTypes.ScheduledTransactionType;
  minimumDate?: DateTime;
  subscriptionInitial?: boolean;
  tabValue: number;
  setTabValue: (_: number) => void;
  dialogActions: any;
  openAssignSplits: () => void;
  rootModalDrawer?: RootModalDrawerOptions;
}
function FormContent({
  formikProps,
  create,
  scheduledTransaction,
  minimumDate,
  subscriptionInitial,
  tabValue,
  setTabValue,
  dialogActions,
  openAssignSplits,
  rootModalDrawer,
}: FormProps) {
  const { classes } = useStyles();

  const formModel = mkScheduledTransaction({
    ...(formikProps.values || {}),
    dueOn: formikProps.values?.dueOn?.toISODate(),
  } as any);
  // BVA needs to track form, not created model status, so it can be available on create
  const showBillTab = useAppSelector(
    (state) =>
      billPresentmentSelectors.getShowBillTab(state, scheduledTransaction as ScheduledTransaction) ||
      billPresentmentSelectors.getCanBVA(state, formModel),
  );

  const handleChange = (_e: unknown, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Form className={classes.form}>
      <Dump value={{ scheduledTransaction, formikProps }} id={scheduledTransaction?.id} />

      <Tabs
        value={tabValue}
        onChange={handleChange}
        aria-label='custom frequency tabs'
        className={classes.tabs}
        variant={rootModalDrawer ? 'fullWidth' : 'standard'}
      >
        <Tab label='Basic Details' />
        <Tab label='Frequency & Occurrence' />
        {showBillTab && <Tab label='Bill Connect' />}
      </Tabs>

      {/* Basic Details */}
      <TabPanel value={tabValue} index={0}>
        <DialogContent id='frm-root-basic'>
          <BasicTab
            formikProps={formikProps}
            scheduledTransaction={scheduledTransaction}
            setTabValue={setTabValue}
            create={create}
            subscriptionInitial={subscriptionInitial}
            showBillConnect={showBillTab}
            onAssignSplits={openAssignSplits}
          />
        </DialogContent>
      </TabPanel>

      {/* Frequency & Occurrence */}
      <TabPanel value={tabValue} index={1}>
        <DialogContent id='frm-root-frequency'>
          <FrequencyTab formikProps={formikProps} minimumDate={minimumDate} />
        </DialogContent>
      </TabPanel>

      {/* Bill Connect */}
      {showBillTab && (
        <TabPanel value={tabValue} index={2}>
          <DialogContent className={classes.billConnect} id='frm-root-bill-connect'>
            <BillConnectTab formikProps={formikProps} rootModalDrawer={rootModalDrawer} />
          </DialogContent>
        </TabPanel>
      )}
      {dialogActions}
    </Form>
  );
}

// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                  Main Component                     - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###

function ScheduledTransactionEditDialog({
  open,
  title = 'Edit Series',
  scheduledTransaction: scheduledTransactionProp,
  onClose, // accepts props 'dirty', and 'forceClose' in order
  onRootModalDrawerDirtyChange,
  onSubmit,
  onBack,
  rootModalDrawer,
  subscriptionInitial = false,
  incomeInitial = false,
  transferInitial = false,
  fakeCreate,
  initialTab = 0,
  initialShowAssignSplits = false,
}: ScheduledTransactionEditDialogProps) {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const model = scheduledTransactionProp;
  // Memoize on `model` so `initialValues` / Formik do not see a new object every render when `model` is an Immutable Record.
  const scheduledTransaction = useMemo(() => toPlainIfImmutable(model) as ScheduledTransactionType, [model]);

  const create = fakeCreate || !model?.id;

  const currentDateString = useAppSelector(dateSelectors.getCurrentDateString);
  const currentDate = useMemo(() => DateTime.fromISO(currentDateString), [currentDateString]);
  const initialDate = useMemo(
    () => getInitialDate(scheduledTransaction, model?.transaction, currentDate),
    [scheduledTransaction, model, currentDate],
  );
  const minimumDate = DateTime.min(initialDate, currentDate);

  const source = window.location.pathname.replaceAll?.('/', '');

  const isDrawer = useAppSelector((state) => featureFlagsSelectors.getFeatureFlags(state).get('webDrawerInput'));
  const splitSeriesFf = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'splitSeries'));

  const incomeCat = useAppSelector((state) =>
    incomeInitial ? categoriesSelectors.getPersonalIncomeCategory(state) : null,
  );
  const [didTrySubmit, setDidTrySubmit] = useState(false);

  const [tabValue, setTabValue] = useState(initialTab);
  const [showAssignSplits, setShowAssignSplits] = useState(Boolean(initialShowAssignSplits && splitSeriesFf));
  const assignSplitsChangedRef = useRef(false);
  const pendingAssignSplitsSplitRef = useRef<PlainSplitProps | null | undefined>(undefined);
  const pendingAssignSplitsCoaRef = useRef<chartOfAccountsTypes.ChartOfAccountProps | null | undefined>(undefined);
  const pendingAssignSplitsTagsRef = useRef<TransactionProps['tags'] | undefined>(undefined);
  const openAssignSplitsDialogRef = useRef<(() => void) | null>(null);
  const openedAssignSplitsDialogIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (open) {
      setShowAssignSplits(Boolean(initialShowAssignSplits && splitSeriesFf));
    }
  }, [initialShowAssignSplits, open, scheduledTransaction?.id, scheduledTransaction?.clientId, splitSeriesFf]);

  useEffect(() => {
    if (!showAssignSplits) {
      openedAssignSplitsDialogIdRef.current = null;
      return;
    }

    const dialogId = `SPLIT_DIALOG_SERIES_${scheduledTransaction?.id || scheduledTransaction?.clientId || 'new'}`;
    if (openedAssignSplitsDialogIdRef.current !== dialogId) {
      openAssignSplitsDialogRef.current?.();
    }
  }, [showAssignSplits, scheduledTransaction?.id, scheduledTransaction?.clientId]);

  // # ===================================================================================== #
  // # -----                               Formik Setup                                ----- #
  // # ===================================================================================== #

  const initialValues: FormikScheduledTxn = useMemo(() => {
    const incomeRecurrence: RecurrenceType = {
      byDay: customFrequencyUtils.indexToWeekday(currentDate.weekday - 1),
      frequency: FrequencyEnum.WEEKLY,
      interval: 2,
    };

    const billRecurrence: RecurrenceType = {
      // For create flow, default monthly recurrence based on the start date month, never literal 29/30/31.
      byMonthDay: [customFrequencyUtils.dayOfMonthToByMonthDay(initialDate)],
      frequency: FrequencyEnum.MONTHLY,
      interval: 1,
    };

    // Sanity Check
    let isExcludedFromF2S = scheduledTransaction?.transaction?.isExcludedFromF2S;
    let isExcludedFromReports = scheduledTransaction?.transaction?.isExcludedFromReports;
    const excludeF2SDest =
      create ||
      (scheduledTransaction &&
        (scheduledTransaction?.excludeF2S === scheduledTransactionsTypes?.STExcludeEnum?.BOTH ||
          scheduledTransaction?.excludeF2S === scheduledTransactionsTypes?.STExcludeEnum?.DEST));
    const excludeReportsDest =
      create ||
      (scheduledTransaction &&
        (scheduledTransaction?.excludeReports === scheduledTransactionsTypes?.STExcludeEnum?.BOTH ||
          scheduledTransaction?.excludeReports === scheduledTransactionsTypes?.STExcludeEnum?.DEST));

    let defaultCoa =
      scheduledTransaction.transaction?.coa ||
      chartOfAccountsTypes.mkChartOfAccount({ id: '0', type: 'UNCATEGORIZED' });
    if (incomeInitial && incomeCat && incomeCat.id && create) {
      defaultCoa = { id: incomeCat.id, type: 'CATEGORY' };
    }
    if (transferInitial && create) {
      defaultCoa = { id: '2', type: 'BALANCE_ADJUSTMENT' };
      isExcludedFromReports = true;
      isExcludedFromF2S = true;
    }

    const spreadTxn = plainSeriesTemplateTransaction(scheduledTransaction);
    const initialTransactionSplit = getInitialSeriesSplitForForm({
      txn: spreadTxn,
      defaultCoa,
      splitSeriesEnabled: Boolean(splitSeriesFf),
    });

    const state = store.getState();
    const isCredit = chartOfAccountsSelectors.isCreditBill(state, spreadTxn?.accountId, defaultCoa);

    // Is transaction of type 'transfer'
    const isTransfer = chartOfAccountsUtils.isTransferCOA(defaultCoa) || isCredit;
    const seriesPrimaryCoa =
      scheduledTransactionsUtils.getSeriesPrimaryCoa(
        spreadTxn ? ({ ...spreadTxn, coa: defaultCoa } as TransactionProps) : null,
      ) ?? defaultCoa;
    const isIncome = Boolean(
      seriesPrimaryCoa?.type === 'CATEGORY' && categoriesSelectors.isIncomeCat(state, seriesPrimaryCoa.id),
    );
    if (create) {
      const excludeIncomeByDefault = preferencesV2Selectors.getPreferenceValue(
        state,
        'sSPExcludeIncomeByDefault',
        false,
      );
      const shouldAutoExclude = transactionsUtils.isAutoExcludedFromF2S(
        defaultCoa,
        excludeIncomeByDefault,
        categoriesSelectors.getIncomeCOAIds(state),
        categoriesSelectors.getBusinessCoaIdsSet(state),
      );
      if (!isIncome && shouldAutoExclude) {
        isExcludedFromF2S = true;
      }
    }
    const defaultRecurrence = scheduledTransaction.recurrence || (isIncome ? incomeRecurrence : billRecurrence);

    const frequencyAlias = customFrequencyUtils.mapRecurrenceFrequencyToFrequencyAlias(defaultRecurrence);
    const endOnDate =
      scheduledTransaction.recurrence?.endOn &&
      (scheduledTransaction.recurrence.endOn instanceof DateTime
        ? scheduledTransaction.recurrence.endOn
        : DateTime.fromISO(scheduledTransaction.recurrence.endOn));
    const virtualEndOn = endOnDate && endOnDate.isValid ? endOnDate : null;

    const immutableTags =
      scheduledTransaction.transaction && scheduledTransaction.transaction.tags
        ? List(scheduledTransaction.transaction?.tags)
        : undefined;

    const formikScheduledTransaction = {
      // all modifiable props should be listed below (formik requires it)
      ...scheduledTransaction,
      dueOn: initialDate,
      transaction: {
        amount: 0,
        payee: '',
        coa: defaultCoa,
        postedOn: initialDate.toISODate(),
        ...spreadTxn,
        ...(spreadTxn?.split ? { split: initialTransactionSplit } : {}),
        accountId: spreadTxn?.accountId || '',
        isExcludedFromF2S,
        isExcludedFromReports,
        excludeF2SDest,
        excludeReportsDest,
        isSubscription: Boolean(spreadTxn?.isSubscription),
        isBill: Boolean(!spreadTxn?.isSubscription && !isIncome),
        isTransfer,
        //  isCredit,
        tags: immutableTags,
      },
      virtualFrequency: frequencyAlias,
      virtualEndOn,
      virtualRecurrence: defaultRecurrence,
      matchCriteria: getInitialAmount(scheduledTransaction),
      autoAdjustAmount: scheduledTransaction.autoAdjustAmount || AutoAdjustAmountEnum.none,
      autoAdjustDueOn: Boolean(create ? false : scheduledTransaction.autoAdjustDueOn),
    };
    return formikScheduledTransaction;
  }, [
    currentDate,
    scheduledTransaction,
    create,
    incomeInitial,
    transferInitial,
    incomeCat,
    store,
    initialDate,
    splitSeriesFf,
  ]);
  // Remount when the series template gains/loses split lines (e.g. Bill Scout hydration) so Formik is not stuck on a pre-split snapshot.
  const seriesSplitTemplateFingerprint = useMemo(
    () => String(splitItemsToArray(plainSeriesTemplateTransaction(scheduledTransaction)?.split?.items ?? []).length),
    [scheduledTransaction],
  );

  // TODO: Move to bit.dev in future during cleanup work?
  // @ts-expect-error FIXME TS7006
  const getExclusions = (values) => {
    let excludeF2S: string = scheduledTransactionsTypes?.STExcludeEnum?.NONE;
    let excludeReports: string = scheduledTransactionsTypes?.STExcludeEnum?.NONE;

    if (values?.coa?.type === 'ACCOUNT') {
      if (values.isExcludedFromF2S && values.excludeF2SDest) {
        excludeF2S = scheduledTransactionsTypes?.STExcludeEnum?.BOTH;
      } else if (!values.isExcludedFromF2S && values.excludeF2SDest) {
        excludeF2S = scheduledTransactionsTypes?.STExcludeEnum?.DEST;
      } else if (values.isExcludedFromF2S && !values.excludeF2SDest) {
        excludeF2S = scheduledTransactionsTypes?.STExcludeEnum?.SRC;
      }

      if (values.isExcludedFromReports && values.excludeReportsDest) {
        excludeReports = scheduledTransactionsTypes?.STExcludeEnum?.BOTH;
      } else if (!values.isExcludedFromReports && values.excludeReportsDest) {
        excludeReports = scheduledTransactionsTypes?.STExcludeEnum?.DEST;
      } else if (values.isExcludedFromReports && !values.excludeReportsDest) {
        excludeReports = scheduledTransactionsTypes?.STExcludeEnum?.SRC;
      }
    } else {
      if (values.isExcludedFromF2S) {
        excludeF2S = scheduledTransactionsTypes?.STExcludeEnum?.SRC;
      }

      if (values.isExcludedFromReports) {
        excludeReports = scheduledTransactionsTypes?.STExcludeEnum?.SRC;
      }
    }

    return {
      excludeF2S,
      excludeReports,
    };
  };

  const getType = (values: TransactionProps) => {
    let type: string = 'income';
    if (values.isBill) type = 'bill';
    if (values.isSubscription) type = 'subscription';
    if (values.isTransfer) type = 'transfer';

    return type;
  };

  const formikSubmit = (values: any, actions: any, force: boolean = false) => {
    const {
      virtualRecurrence,
      dueOn,
      virtualEndOn,
      virtualFrequency,
      transaction,
      id,
      matchCriteria,
    }: FormikScheduledTxn = values || {};
    const isValidRecurrence = customFrequencyUtils.isValidRecurrence(virtualRecurrence);

    // The following set of if conditions specifically checks reqiured field values on submit.  Ideally this would be done via form field validation, but since
    // fields are on different tabs, Formik does not handle this well.  In the future, we should convert this entire form to react-hook-form and react-hook-form-mui
    const errs = { virtualRecurrence: '', dueOn: '', virtualEndOn: '' };
    if (!isValidRecurrence) {
      errs.virtualRecurrence = 'Required';
    }

    const startDateMissingMessage = !dueOn ? 'Required' : '';
    if (startDateMissingMessage.length > 0) {
      errs.dueOn = startDateMissingMessage;
    }

    const minDueOn = DateTime.min(initialValues.dueOn, currentDate);

    if (dueOn < minDueOn) {
      errs.dueOn = `Date must be between ${minDueOn.toFormat('D')}`;
    }

    if (virtualEndOn && virtualEndOn < dueOn) {
      errs.virtualEndOn = `Date must be on or after ${dueOn.toFormat('D')}`;
    }

    // if there are any errors on the given fields, set those errors and switch the correct tab.
    if (!force && (errs.virtualRecurrence !== '' || errs.dueOn !== '' || errs.virtualEndOn !== '')) {
      actions.setErrors(errs);
      actions.setSubmitting(false);
      setTabValue(1);
      return;
    }

    if (customFrequencyUtils.verifyValues(values)) {
      // both the below dates are formatted in such a way because if you send an ISO QCS sometimes won't generate models
      // for same-day because it treats them as in the past depending on the time of day
      const stDueOn = dueOn?.isValid ? dueOn.toFormat('yyyy-MM-dd') : undefined;
      // const endOn = virtualEndOn?.isValid && virtualEndOn.toFormat('yyyy-MM-dd');

      const valTxn = { ...(transaction || {}) } as SubmittedSeriesTransaction;
      const hasSplitItems = splitItemsToArray(valTxn.split?.items ?? []).length > 0;
      if (hasSplitItems) {
        // When a series has assigned splits, the "main" category is not applicable.
        // Align with transaction-detail behavior (coa cleared when split exists).
        valTxn.coa = null;
        valTxn.customerId = null;
        valTxn.projectId = null;
        valTxn.isBillable = false;
        valTxn.split = normalizeSeriesSplitForPersistence(valTxn.split);
      }

      // For create or edit series flow, ensure that we do not send invalid value of businessId field during QCS call
      if (valTxn.businessId === 'PERSONAL') {
        valTxn.businessId = '';
      }

      const count = virtualFrequency === 'One-time payment' ? 1 : undefined;

      const state = store.getState();
      const isCredit = valTxn && chartOfAccountsSelectors.isCreditBill(state, valTxn.accountId, valTxn.coa);
      const incomeCOAIds = categoriesSelectors.getIncomeCOAIds(state);
      const isIncome = scheduledTransactionsUtils.isIncomeSeriesFromTxn(valTxn, incomeCOAIds);
      const account = valTxn.accountId ? accountsSelectors.getAccountById(state, valTxn.accountId) : null;
      const excludeIncomeByDefault = preferencesV2Selectors.getPreferenceValue(
        state,
        'sSPExcludeIncomeByDefault',
        false,
      );
      const excludeF2SUserOverridden = Boolean(valTxn.excludeF2SUserOverridden);

      // Is transaction of type 'transfer'
      const isTransfer = chartOfAccountsUtils.isTransferCOA(valTxn.coa) || isCredit;

      if (isIncome) {
        valTxn.isTransfer = false;
        valTxn.isBill = false;
        valTxn.isSubscription = false;
        if (
          shouldKeepManualIncomeIncludedOnSubmit({
            create,
            isIncome,
            excludeIncomeByDefault,
            excludeF2SUserOverridden,
            accountExcludedFromBudgets: Boolean(account?.isExcludedFromBudgets),
          })
        ) {
          // Create-income defaults display as included while the user has not touched
          // the Spending Plan override. Keep the submit payload aligned with that UI state.
          valTxn.isExcludedFromF2S = false;
          valTxn.excludeF2SDest = false;
        }
      } else if (isTransfer) {
        valTxn.isTransfer = true;
        valTxn.isBill = false;
        valTxn.isSubscription = false;
      }
      const { excludeF2S, excludeReports } = getExclusions(valTxn);
      const type = getType(valTxn);

      // Scheduled-series "template" transactions often don't have a real transaction id.
      // Avoid sending `transaction.id: null` in the request payload (prefer omitting the field).
      const { excludeF2SUserOverridden: _excludeF2SUserOverridden, ...txnForRequest } = valTxn;
      const normalizedTxn: TransactionProps = {
        ...txnForRequest,
        id: txnForRequest.id ?? undefined,
      };
      const newTxn = transactionsUtils.updateNewTags(transactionsTypes.mkCashFlowTransaction(normalizedTxn), true);
      const newRecurrence = customFrequencyUtils.recurrenceFromVirtualRecurrence(virtualRecurrence);
      const newRecurrenceRecord = scheduledTransactionsTypes.mkRecurrence({
        ...newRecurrence,
        endOn: virtualEndOn,
        count,
      });

      if (id) {
        dispatch(
          scheduledTransactionsActions.updateScheduledTransaction(
            scheduledTransactionsTypes.mkScheduledTransactionPatch({
              ...values,
              excludeF2S,
              excludeReports,
              dueOn: stDueOn,
              type: scheduledTransactionsTypes.STTypeEnum.UNKNOWN, // to avoid conflicts like BILL + isSubscription conflict
              recurrence: newRecurrenceRecord,
              transaction: newTxn,
              ...(fakeCreate && { isUserVerified: true }),
            }),
            {
              ...(fakeCreate ? {} : { undo: { userMessage: 'Recurring transaction updated.' } }),
            },
          ),
        );
      } else {
        dispatch(
          scheduledTransactionsActions.createScheduledTransaction(
            scheduledTransactionsTypes.mkScheduledTransaction({
              clientId: uuidv4().toUpperCase(), // backup for irresponsible developers
              ...values,
              excludeF2S,
              excludeReports,
              dueOn: stDueOn,
              type: scheduledTransactionsTypes.STTypeEnum.UNKNOWN, // to avoid conflict like BILL + isSubscription conflict
              isUserVerified: true,
              recurrence: newRecurrenceRecord,
              transaction: newTxn,
            }),
            { undo: { userMessage: 'Recurring transaction created.' } },
          ),
        );
      }
      const frequency = newRecurrence.alias?.toLowerCase().replaceAll?.('_', ' ') || 'Unknown';
      tracker.track(tracker.events.changeSeries, {
        source,
        purpose: create ? 'create' : 'edit',
        frequency,
        frequencyDetails: newRecurrence,
        type: type,
        frequencyState: newRecurrence?.alias ? 'changed' : 'default',
        matchCriteria: matchCriteria?.toLowerCase(),
        excludeFromSpendingPlan: transaction?.isExcludedFromF2S,
        excludeFromReports: transaction?.isExcludedFromReports,
        payee: transaction?.payee,
        category: transaction?.coa?.id
          ? chartOfAccountsSelectors.getCoaStringSelector(null, transaction?.coa, true)
          : 'uncategorized',
      });
      actions.setSubmitting?.(false);
      if (onSubmit) {
        onSubmit();
      } else {
        onClose(false);
      }
    } else {
      transaction && actions.setErrors?.(customFrequencyUtils.setErrorFields(transaction as TransactionProps));
      setTabValue(0);
    }
  };

  // # ===================================================================================== #
  // # -----                            Action Functions                               ----- #
  // # ===================================================================================== #
  const deleteFunction = () => {
    dispatch(
      scheduledTransactionsActions.deleteScheduledTransaction(
        scheduledTransactionsTypes.mkScheduledTransactionPatch({
          clientId: model?.clientId,
          id: model?.id,
          isDeleted: true,
        }),
        { undo: { userMessage: 'Recurring transaction deleted.' } },
      ),
    );
    onClose(false, true);
  };

  const confirmDeleteFunction = () => {
    showConfirmationDialog({
      id: 'delete-series-confirmation-edit-series',
      size: 'sm',
      title: 'Delete recurring series',
      content: `Deleting the recurring series for ${model?.transaction?.payee} will delete all future reminders for this series.`,
      confirmLabel: 'Delete',
      confirmButtonVariant: 'delete',
      confirmAction: deleteFunction,
      denyLabel: 'Cancel',
      denyButtonVariant: 'outlined',
    });
  };

  return (
    <Formik<FormikScheduledTxn>
      key={`${model?.id || model?.clientId || 'new'}-${seriesSplitTemplateFingerprint}`}
      enableReinitialize
      initialValues={initialValues}
      validateOnChange={didTrySubmit}
      validateOnBlur={false}
      onSubmit={formikSubmit}
    >
      {(formikProps) => {
        const { dirty } = formikProps || {};

        const transaction = (formikProps.values?.transaction || {}) as TransactionProps;
        const splitRecord = transaction.split
          ? (transactionsTypes.mkSplit(transaction.split as SplitProps) as RecordOf<SplitProps>)
          : null;

        const noopSetCustomersAndProjects: React.Dispatch<React.SetStateAction<ProjectSelect>> = (_value) => {
          // no-op: series split assignment does not support client/project selection
        };

        const setSeriesSplit = (val: RecordOf<SplitProps> | null) => {
          assignSplitsChangedRef.current = true;
          if (!val) {
            // Explicit clear signal for API.
            const nextSplit = { items: null } satisfies PlainSplitProps;
            pendingAssignSplitsSplitRef.current = nextSplit;
            formikProps.setFieldValue('transaction.split', nextSplit);
            return;
          }
          const rawSplit = splitToPlain(val);
          const nextSplit = rawSplit
            ? stripOverflowMemoFromSplit(rawSplit)
            : ({ items: null } satisfies PlainSplitProps);
          pendingAssignSplitsSplitRef.current = nextSplit;
          formikProps.setFieldValue('transaction.split', nextSplit);
        };

        const txnForSplitDialog: TransactionProps = {
          ...transaction,
          split: splitRecord ?? undefined,
        };

        const assignSplitsDialogId = `SPLIT_DIALOG_SERIES_${scheduledTransaction?.id || scheduledTransaction?.clientId || 'new'}`;
        const closeSplitDialog = () => dispatch(rootUiActions.removeDialog(assignSplitsDialogId, { animate: true }));
        const onBackToScheduledTxn = () => {
          // If we deep-linked directly into Assign Splits (e.g., from Edit Reminder),
          // treat "Back" as "done" and close the series dialog so the user returns
          // to the previous modal (Edit Reminder), not the Edit Series UI.
          if (initialShowAssignSplits) {
            // If splits changed (e.g., Delete All Splits), submit the series update now.
            if (assignSplitsChangedRef.current) {
              const valuesToSubmit = {
                ...formikProps.values,
                transaction: {
                  ...formikProps.values.transaction,
                  split: pendingAssignSplitsSplitRef.current ?? formikProps.values.transaction?.split,
                  coa: pendingAssignSplitsCoaRef.current ?? formikProps.values.transaction?.coa,
                  tags: pendingAssignSplitsTagsRef.current ?? formikProps.values.transaction?.tags,
                },
              };
              formikSubmit(valuesToSubmit, formikProps, true);
              return;
            }
            onClose(false);
            return;
          }
          setShowAssignSplits(false);
        };

        const openAssignSplitsDialog = () => {
          if (!splitSeriesFf) {
            return;
          }

          assignSplitsChangedRef.current = false;
          pendingAssignSplitsSplitRef.current = undefined;
          pendingAssignSplitsCoaRef.current = undefined;
          pendingAssignSplitsTagsRef.current = undefined;
          openedAssignSplitsDialogIdRef.current = assignSplitsDialogId;
          setShowAssignSplits(true);

          dispatch(
            showSplitDialog({
              id: assignSplitsDialogId,
              mode: 'series',
              txn: txnForSplitDialog,
              setSplit: setSeriesSplit,
              setAmount: (val: number) => formikProps.setFieldValue('transaction.amount', val),
              setCoa: (newCOA) => {
                pendingAssignSplitsCoaRef.current = newCOA ?? null;
                formikProps.setFieldValue('transaction.coa', newCOA ?? null);
              },
              setTags: (val) => {
                pendingAssignSplitsTagsRef.current = val;
                formikProps.setFieldValue('transaction.tags', val);
              },
              accountId: transaction.accountId ?? '',
              disabledPaymentFn: () => !doesSplitModeAllowPaymentOrnaments('series'),
              setCustomersAndProjects: noopSetCustomersAndProjects,
              rootModalDrawer: rootModalDrawer
                ? {
                    ...rootModalDrawer,
                  }
                : undefined,
              onBack: () => {
                closeSplitDialog();
                onBackToScheduledTxn();
              },
              onClose: () => {
                closeSplitDialog();
                onClose(dirty);
              },
            }),
          );
        };
        openAssignSplitsDialogRef.current = openAssignSplitsDialog;

        const formContent = (
          <FormContent
            formikProps={formikProps}
            scheduledTransaction={scheduledTransaction}
            minimumDate={minimumDate}
            rootModalDrawer={rootModalDrawer}
            subscriptionInitial={subscriptionInitial}
            tabValue={tabValue}
            setTabValue={setTabValue}
            openAssignSplits={() => {
              if (splitSeriesFf) {
                openAssignSplitsDialog();
              }
            }}
            dialogActions={
              // buttons must be inside Form to function properly
              <DialogActions>
                <DevMenuButton id='recurrence-dev-menu'>
                  <MenuItem onClick={() => formikSubmit(formikProps.values, {}, true)}>Force Series Submit</MenuItem>
                  <MenuItem
                    onClick={() =>
                      dispatchRequestDialog({
                        method: 'PUT',
                        path: `/scheduled-transactions/${formikProps.values?.id}`,
                        body: JSON.stringify(formikProps.values, null, 2),
                      })
                    }
                  >
                    Manual Series API Call
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      const txns = [
                        {
                          ...formikProps.values.transaction,
                          state: 'CLEARED',
                        },
                      ];
                      dispatchGeneratorDialog({ txns, start: formikProps.values.dueOn?.toISODate() });
                    }}
                  >
                    Generate Series Instances
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      const currentTxn = formikProps.values.transaction;
                      const txns = [
                        {
                          ...currentTxn,
                          state: 'CLEARED',
                        },
                        {
                          ...currentTxn,
                          accountId: currentTxn.coa?.id,
                          coa: { type: 'ACCOUNT', id: currentTxn.accountId },
                          amount: -currentTxn.amount,
                          state: 'CLEARED',
                        },
                      ];
                      dispatchGeneratorDialog({ txns, start: formikProps.values.dueOn?.toISODate() });
                    }}
                  >
                    Generate Series Transfer Instances
                  </MenuItem>
                </DevMenuButton>

                <QButton
                  id='delete-recurrence'
                  variant={create ? 'outlined' : 'delete'}
                  onClick={create ? () => onClose(dirty) : confirmDeleteFunction}
                  disableOutline
                >
                  {create ? 'Cancel' : 'Delete'}
                </QButton>

                <QButton
                  id='create-update-recurrence'
                  type='submit'
                  variant='contained'
                  onClick={() => setDidTrySubmit(true)}
                >
                  {create ? 'Create' : 'Update'}
                </QButton>
              </DialogActions>
            }
            create={create}
          />
        );

        if (rootModalDrawer) {
          return (
            <ScheduledTransactionRootModalDrawer
              isDirty={dirty}
              onBack={onBack}
              onClose={() => onClose(false)}
              onRootModalDrawerDirtyChange={onRootModalDrawerDirtyChange}
              open={open}
              title={title}
            >
              {formContent}
            </ScheduledTransactionRootModalDrawer>
          );
        }

        if (isDrawer) {
          return (
            <>
              <Drawer
                aria-labelledby='edit-recurrence-dialog'
                open={open && !showAssignSplits}
                anchor='right'
                onClose={() => onClose(dirty)}
                PaperProps={{
                  sx: { width: '50%' },
                }}
                sx={getDrawerSx()}
              >
                <div className={classes.frequencyHeader}>
                  <div className={classes.titleText}>{title}</div>
                  <div className={classes.closeButton}>
                    <IconButton aria-label='Close' id='dlg-close' onClick={() => onClose(false)}>
                      <CloseIcon />
                    </IconButton>
                  </div>
                </div>
                {formContent}
              </Drawer>
            </>
          );
        }

        return (
          <>
            <StdDialog
              aria-labelledby='edit-recurrence-dialog'
              onBack={onBack}
              onClose={() => onClose(dirty)}
              open={open && !showAssignSplits}
              showBackButton={Boolean(onBack)}
              size='md'
              title={title}
            >
              {formContent}
            </StdDialog>
          </>
        );
      }}
    </Formik>
  );
}

export default ScheduledTransactionEditDialog;
